/**
 * @fileoverview User Management flash message.
 * @description The green confirmation banner at the top of the page (a user added, updated,
 * deactivated or reactivated, an invitation re-sent or revoked), with a button to dismiss it.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

interface UserManagementFlashProps {
  message: string
  onDismiss: () => void
}

/**
 * @component
 * @param {UserManagementFlashProps} props - The message, and what dismissing it does.
 * @param {string} props.message - The confirmation to show.
 * @param {() => void} props.onDismiss - Dismisses the banner.
 */
function UserManagementFlash({ message, onDismiss }: UserManagementFlashProps) {
  const { t } = useTranslation()

  return (
    <div
      data-testid="user-management-flash"
      className="flex items-center gap-2.5 rounded-lg border border-success/30 bg-success-bg px-3.5 py-2.5"
    >
      <span className="flex-1 text-[13px] font-medium text-success">{message}</span>
      <button
        type="button"
        onClick={onDismiss}
        data-testid="user-management-flash-dismiss-button"
        aria-label={t('sales.userManagement.flash.dismiss')}
        className="shrink-0 cursor-pointer text-success"
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
        </svg>
      </button>
    </div>
  )
}

export default UserManagementFlash
